/**
 * useSourceMapReview — Data hooks for source coordinate map review
 *
 * Provides Apollo Client hooks for:
 * - Listing source maps for a tenant
 * - Getting a single source map by ID
 * - Verifying/rejecting individual extractions
 * - Bulk-verifying high-confidence extractions
 */

import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';
import type { SourceCoordinateMap } from '../components/source-map/ExtractionReviewDashboard';

// ============================================================================
// GraphQL Operations
// ============================================================================

const GET_SOURCE_MAP = gql`
  query GetSourceMap($tenantId: String!, $mappingId: String!) {
    sourceMap(tenantId: $tenantId, mappingId: $mappingId) {
      mappingId
      sourceDocument {
        documentId
        filename
        totalPages
      }
      ingestedAt
      extractor {
        provider
        model
      }
      reviewStatus
      extractions {
        id
        source {
          page
          sectionHeading
          quotedText
        }
        extracted {
          field
          value
          valueType
          ediContext {
            segment
            loop
            element
            qualifier
          }
        }
        target {
          layer
          sourceFile
          sourcePath
          sourceLine
        }
        confidence
        annotationColor
        verificationStatus
        verifiedBy
        correctionNote
      }
      summary {
        totalExtractions
        verifiedCount
        rejectedCount
        unverifiedCount
        averageConfidence
      }
    }
  }
`;

const LIST_SOURCE_MAPS = gql`
  query ListSourceMaps($filter: SourceMapFilterInput!) {
    sourceMaps(filter: $filter) {
      mappingId
      sourceDocument {
        documentId
        filename
        totalPages
      }
      ingestedAt
      extractor {
        provider
        model
      }
      reviewStatus
      summary {
        totalExtractions
        verifiedCount
        rejectedCount
        unverifiedCount
        averageConfidence
      }
    }
  }
`;

const GET_SOURCE_MAP_STATS = gql`
  query GetSourceMapStats($tenantId: String!) {
    sourceMapStats(tenantId: $tenantId) {
      totalMaps
      pendingReview
      verified
      totalExtractions
      verifiedExtractions
      averageConfidence
    }
  }
`;

const VERIFY_EXTRACTION = gql`
  mutation VerifyExtraction($input: VerifyExtractionInput!) {
    verifyExtraction(input: $input) {
      id
      verificationStatus
      verifiedBy
      correctionNote
    }
  }
`;

const REJECT_EXTRACTION = gql`
  mutation RejectExtraction($input: RejectExtractionInput!) {
    rejectExtraction(input: $input) {
      id
      verificationStatus
      verifiedBy
      correctionNote
    }
  }
`;

const BULK_VERIFY = gql`
  mutation BulkVerifyExtractions($input: BulkVerifyInput!) {
    bulkVerifyExtractions(input: $input) {
      mappingId
      summary {
        totalExtractions
        verifiedCount
        rejectedCount
        unverifiedCount
        averageConfidence
      }
    }
  }
`;

// ============================================================================
// Hooks
// ============================================================================

interface SourceMapListItem {
  mappingId: string;
  sourceDocument: {
    documentId: string;
    filename: string;
    totalPages: number | null;
  };
  ingestedAt: string;
  extractor: {
    provider: string;
    model: string;
  };
  reviewStatus: string;
  summary: {
    totalExtractions: number;
    verifiedCount: number;
    rejectedCount: number;
    unverifiedCount: number;
    averageConfidence: number;
  };
}

interface SourceMapStats {
  totalMaps: number;
  pendingReview: number;
  verified: number;
  totalExtractions: number;
  verifiedExtractions: number;
  averageConfidence: number;
}

/** Get a single source map by ID */
export function useSourceMap(tenantId: string, mappingId: string) {
  const { data, loading, error, refetch } = useQuery<{
    sourceMap: SourceCoordinateMap | null;
  }>(GET_SOURCE_MAP, {
    variables: { tenantId, mappingId },
    skip: !tenantId || !mappingId,
  });

  return {
    sourceMap: data?.sourceMap ?? null,
    loading,
    error: error?.message ?? null,
    refetch,
  };
}

/** List all source maps for a tenant */
export function useSourceMaps(tenantId: string, reviewStatus?: string) {
  const { data, loading, error, refetch } = useQuery<{
    sourceMaps: SourceMapListItem[];
  }>(LIST_SOURCE_MAPS, {
    variables: {
      filter: {
        tenantId,
        reviewStatus: reviewStatus ?? null,
        limit: 50,
      },
    },
    skip: !tenantId,
  });

  return {
    sourceMaps: data?.sourceMaps ?? [],
    loading,
    error: error?.message ?? null,
    refetch,
  };
}

/** Get stats across all source maps */
export function useSourceMapStats(tenantId: string) {
  const { data, loading, error } = useQuery<{
    sourceMapStats: SourceMapStats;
  }>(GET_SOURCE_MAP_STATS, {
    variables: { tenantId },
    skip: !tenantId,
  });

  return {
    stats: data?.sourceMapStats ?? null,
    loading,
    error: error?.message ?? null,
  };
}

/** Verify a single extraction */
export function useVerifyExtraction() {
  const [mutate, { loading, error }] = useMutation(VERIFY_EXTRACTION);

  const verify = async (
    tenantId: string,
    mappingId: string,
    extractionId: string,
    reviewerId: string,
    status: 'CONFIRMED' | 'CORRECTED',
    correctionNote?: string,
  ) => {
    return mutate({
      variables: {
        input: {
          tenantId,
          mappingId,
          extractionId,
          reviewerId,
          status,
          correctionNote,
        },
      },
    });
  };

  return { verify, loading, error: error?.message ?? null };
}

/** Reject a single extraction */
export function useRejectExtraction() {
  const [mutate, { loading, error }] = useMutation(REJECT_EXTRACTION);

  const reject = async (
    tenantId: string,
    mappingId: string,
    extractionId: string,
    reviewerId: string,
    reason: string,
  ) => {
    return mutate({
      variables: {
        input: {
          tenantId,
          mappingId,
          extractionId,
          reviewerId,
          reason,
        },
      },
    });
  };

  return { reject, loading, error: error?.message ?? null };
}

/** Bulk-verify all high-confidence extractions */
export function useBulkVerifyExtractions() {
  const [mutate, { loading, error }] = useMutation(BULK_VERIFY);

  const bulkVerify = async (
    tenantId: string,
    mappingId: string,
    reviewerId: string,
    confidenceThreshold = 0.9,
  ) => {
    return mutate({
      variables: {
        input: {
          tenantId,
          mappingId,
          reviewerId,
          confidenceThreshold,
        },
      },
    });
  };

  return { bulkVerify, loading, error: error?.message ?? null };
}
